import { addDays, format, getDay, isValid, parse } from 'date-fns'

import type { Nutrients } from './utils/nutrients'

// GLOBAL
export const APP_ERROR_TITLE = 'Wystąpił błąd aplikacji'
export const APP_ERROR_CONTENT = `Odśwież stronę i spróbuj ponownie. W przypadku dodatkowych pytań skontaktuj się z
                                  nami pod adresem kontakt@kcalmar.com.`
export const OFFLINE_ERROR_TITLE = 'Błąd połączenia internetowego'
export const OFFLINE_ERROR_CONTENT = `Ups... Wygląda na to, że nie masz połączenia z internetem. Zmiany wprowadzane w
                                      aplikacji nie zostaną zapisane. Sprawdź połączenie internetowe i spróbuj
                                      odświeżyć stronę.`
export const SERVER_ERROR_TITLE = 'Wystąpił błąd serwera'
export const SERVER_ERROR_CONTENT = `Ups... Wygląda na to, że wystąpił błąd serwera. Zmiany wprowadzane w
                                     aplikacji mogą nie zostać zapisane. Odśwież stronę i spróbuj ponownie. W przypadku
                                     dodatkowych pytań skontaktuj się z nami pod adresem kontakt@kcalmar.com.`

export const BROWSER_ERROR_TITLE = 'Nieprawidłowa przeglądarka'
export const BROWSER_ERROR_CONTENT = `Ups... Twoja przeglądarka nie wspiera technologii Web Worker. Spróbuj użyć innej
                                      przeglądarki. W przypadku dodatkowych pytań skontaktuj się z nami pod adresem
                                      kontakt@kcalmar.com.`

export const WORKER_ERROR_TITLE = 'Wystąpił błąd Web Worker'
export const WORKER_ERROR_CONTENT = `Ups... Wystąpił błąd Web Workera. Aplikacja może nie działać prawidłowo. Odśwież
                                     stronę i spróbuj ponownie. W przypadku dodatkowych pytań skontaktuj się z nami pod
                                     adresem kontakt@kcalmar.com.`

export const daysMap = [
  'Poniedziałek',
  'Wtorek',
  'Środa',
  'Czwartek',
  'Piątek',
  'Sobota',
  'Niedziela',
] as const
export const DIET_MIN_DAYS_COUNT = 1
export const DIET_MAX_DAYS_COUNT = 31
export const DIET_DEFAULT_DAYS_COUNT = 7
/** @deprecated Use getDietDaysCount / DIET_DEFAULT_DAYS_COUNT / DIET_MAX_DAYS_COUNT */
export const DIET_DAYS_COUNT = DIET_DEFAULT_DAYS_COUNT
export const DIET_WEEK_DAYS_COUNT = 7
export const DIET_AVERAGE_COLUMN_WIDTH_PX = 30
export const DIET_AVERAGE_COLUMN_EXPANDED_WIDTH_PX = 142
export const daysNumericMap = Array.from(
  { length: DIET_MAX_DAYS_COUNT },
  (_, index) => `Dzień ${index + 1}`,
)
export const WEEK_DAYS_INDEX = Array.from({ length: DIET_WEEK_DAYS_COUNT }, (_, index) => index)

export const clampDietDaysCount = (daysCount: number) =>
  Math.min(DIET_MAX_DAYS_COUNT, Math.max(DIET_MIN_DAYS_COUNT, daysCount))

export const getDietWeeksCount = (daysCount: number) =>
  Math.ceil(clampDietDaysCount(daysCount) / DIET_WEEK_DAYS_COUNT)

export const getDaysIndex = (daysCount: number) =>
  Array.from({ length: clampDietDaysCount(daysCount) }, (_, index) => index)

export const hours = [
  '00:00',
  '00:30',
  '01:00',
  '01:30',
  '02:00',
  '02:30',
  '03:00',
  '03:30',
  '04:00',
  '04:30',
  '05:00',
  '05:30',
  '06:00',
  '06:30',
  '07:00',
  '07:30',
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '12:00',
  '12:30',
  '13:00',
  '13:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
  '18:00',
  '18:30',
  '19:00',
  '19:30',
  '20:00',
  '20:30',
  '21:00',
  '21:30',
  '22:00',
  '22:30',
  '23:00',
  '23:30',
] as const

export const mealTypes = [
  { text: 'Śniadanie', value: 1 },
  { text: 'Drugie śniadanie', value: 2 },
  { text: 'Obiad', value: 3 },
  { text: 'Podwieczorek', value: 4 },
  { text: 'Kolacja', value: 5 },
  { text: 'Przekąska', value: 6 },
] as const

export const DEFAULT_NUTRIENTS: Nutrients[] = [
  'protein',
  'fat',
  'carbohydrates',
  'fiber',
  'carbohydrate_exchanger',
] as const

/**
 * Zwraca nazwę dnia do wyświetlenia w zależności od typu diety i start_date
 * @param dayIndex - Indeks dnia (0-based)
 * @param dietType - Typ diety ('numeric' | 'days_of_week')
 * @param startDate - Data rozpoczęcia diety w formacie 'dd-MM-yyyy' (opcjonalnie)
 * @returns Nazwa dnia do wyświetlenia
 */
export function getDayDisplayName(
  dayIndex: number,
  dietType: 'numeric' | 'days_of_week' | undefined,
  startDate?: string,
): string {
  if (dietType === 'numeric') {
    return daysNumericMap[dayIndex] ?? `Dzień ${dayIndex + 1}`
  }

  // days_of_week bez start_date
  if (!startDate?.includes('-')) {
    return daysMap[dayIndex % daysMap.length] ?? ''
  }

  // days_of_week z start_date - oblicz przesunięcie
  const date = parse(startDate, 'dd-MM-yyyy', new Date())
  const jsDay = getDay(date) // 0=niedziela, 1=poniedziałek, ..., 6=sobota
  const startDayIndex = jsDay === 0 ? 6 : jsDay - 1 // konwersja do daysMap (0=poniedziałek)
  const shiftedDayIndex = (startDayIndex + dayIndex) % 7
  return daysMap[shiftedDayIndex] ?? ''
}

export type ScheduleDayHeader = {
  date?: string
  name: string
  text: string
  shortName: string
}

export function getScheduleDayHeader(dayIndex: number, startDate?: string): ScheduleDayHeader {
  const fallback = daysNumericMap[dayIndex] ?? `Dzień ${dayIndex + 1}`
  const parsedStartDate = startDate ? parse(startDate, 'dd-MM-yyyy', new Date()) : undefined

  if (
    !parsedStartDate ||
    !isValid(parsedStartDate) ||
    format(parsedStartDate, 'dd-MM-yyyy') !== startDate
  ) {
    return { name: fallback, shortName: fallback, text: fallback }
  }

  const date = addDays(parsedStartDate, dayIndex)
  const jsDay = getDay(date)
  const name = daysMap[jsDay === 0 ? 6 : jsDay - 1] ?? fallback
  const formattedDate = format(date, 'dd.MM')
  const shortName = format(date, 'iii dd.MM')

  return { date: formattedDate, name, shortName, text: `${name} ${formattedDate}` }
}
